import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconUserGroup, IconUserAdd, IconCart, IconCreditCard } from '@douyinfe/semi-icons';
import { Card, Spin } from '@douyinfe/semi-ui';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import styles from './index.module.scss';
import request from '../../utils/request';

// 配置化的统计卡片定义
const STAT_CARDS_CONFIG = [
  {
    id: 'total_users',
    title: '总用户数',
    icon: IconUserGroup,
    color: 'var(--semi-color-primary)',
    type: 1,
    dateType: 1000, // 累计数据
    format: 'number',
    navigateTo: '/users'
  },
  {
    id: 'yesterday_new_users',
    title: '昨日新增',
    icon: IconUserAdd,
    color: 'var(--semi-color-success)',
    type: 1,
    dateType: 2, // 昨日数据
    format: 'number',
    navigateTo: '/users'
  },
  {
    id: 'today_users',
    title: '今日新用户',
    icon: IconUserGroup,
    color: 'var(--semi-color-warning)',
    type: 1,
    dateType: 1, // 今日数据
    format: 'number',
    navigateTo: '/users'
  },
  {
    id: 'yesterday_users',
    title: '昨日新用户',
    icon: IconUserGroup,
    color: 'var(--semi-color-info)',
    type: 1,
    dateType: 2, // 昨日数据
    format: 'number',
    navigateTo: '/users'
  },
  {
    id: 'today_orders',
    title: '今日订单量',
    icon: IconCart,
    color: 'var(--semi-color-primary)',
    type: 2,
    dateType: 1, // 今日数据
    format: 'number',
    navigateTo: '/orders'
  },
  {
    id: 'yesterday_orders',
    title: '昨日订单量',
    icon: IconCart,
    color: 'var(--semi-color-success)',
    type: 2,
    dateType: 2, // 昨日数据
    format: 'number',
    navigateTo: '/orders'
  },
  {
    id: 'today_amount',
    title: '今日订单金额',
    icon: IconCreditCard,
    color: 'var(--semi-color-warning)',
    type: 3,
    dateType: 1, // 今日数据
    format: 'amount',
    navigateTo: '/orders'
  },
  {
    id: 'yesterday_amount',
    title: '昨日订单金额',
    icon: IconCreditCard,
    color: 'var(--semi-color-info)',
    type: 3,
    dateType: 2, // 昨日数据
    format: 'amount',
    navigateTo: '/orders'
  }
];

// 配置化的图表定义
const CHARTS_CONFIG = [
  {
    id: 'users_trend',
    title: '最近用户量趋势',
    type: 1,
    dateType: 1,
    color: '#165DFF',
    format: 'number'
  },
  {
    id: 'orders_trend',
    title: '最近订单量趋势',
    type: 2,
    dateType: 1,
    color: '#00B42A',
    format: 'number'
  },
  {
    id: 'amount_trend',
    title: '最近订单金额趋势',
    type: 3,
    dateType: 1,
    color: '#FF7D00',
    format: 'amount'
  }
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [chartData, setChartData] = useState({});
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [chartsError, setChartsError] = useState(false);

  // 获取单个统计数据
  const fetchStatOne = async (type, dateType) => {
    try {
      const response = await request.get('/go/sp/stat/get_stat_one/', {
        params: { typ: type, date_typ: dateType }
      });

      if (response.errno === 0) {
        const value = response.stat_data?.value || 0;
        return response.dateType === 3 ? parseInt(value) : parseFloat(value);
      }
      return 0;
    } catch (error) {
      console.error(`获取统计数据失败 (type: ${type}, dateType: ${dateType}):`, error);
      return 0;
    }
  };

  // 获取趋势数据
  const fetchStatList = async (type, dateType) => {
    try {
      const response = await request.get('/go/sp/stat/get_stat_list/', {
        params: { typ: type, date_typ: dateType }
      });

      if (response.errno === 0 && response.stat_data) {
        return response.stat_data.values?.map(item => ({
          name: item.name,
          value: parseFloat(item.value || 0)
        })) || [];
      }
      return [];
    } catch (error) {
      console.error(`获取趋势数据失败 (type: ${type}, dateType: ${dateType}):`, error);
      return [];
    }
  };

  // 获取所有统计数据
  const fetchAllStats = async () => {
    try {
      setLoading(true);

      const statsPromises = STAT_CARDS_CONFIG.map(async (card) => {
        const value = await fetchStatOne(card.type, card.dateType);
        return { [card.id]: value };
      });

      const statsResults = await Promise.all(statsPromises);
      const newStats = statsResults.reduce((acc, stat) => ({ ...acc, ...stat }), {});

      setStats(newStats);
    } catch (error) {
      console.error('获取统计数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取所有趋势数据
  const fetchAllTrendData = async () => {
    try {
      setChartsLoading(true);
      setChartsError(false);

      const chartsPromises = CHARTS_CONFIG.map(async (chart) => {
        const data = await fetchStatList(chart.type, chart.dateType);
        return { [chart.id]: data };
      });

      const chartsResults = await Promise.all(chartsPromises);
      const newChartData = chartsResults.reduce((acc, chart) => ({ ...acc, ...chart }), {});

      setChartData(newChartData);
    } catch (error) {
      console.error('获取趋势数据失败:', error);
      setChartsError(true);
    } finally {
      setChartsLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      await fetchAllStats();
      await fetchAllTrendData();
    };

    initializeData();
  }, []);

  const handleCardClick = (navigateTo) => {
    navigate(navigateTo);
  };

  const formatValue = (value, format) => {
    if (format === 'amount') {
      return `¥${value.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return value.toLocaleString();
  };

  const renderChart = (data, title, color = '#165DFF', format = 'number') => {
    if (chartsLoading) {
      return (
        <div style={{
          height: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Spin size="large" />
        </div>
      );
    }

    if (chartsError) {
      return (
        <div style={{
          height: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--semi-color-danger)',
          flexDirection: 'column',
          gap: '8px'
        }}>
          <div>数据加载失败</div>
          <button
            onClick={fetchAllTrendData}
            style={{
              padding: '4px 12px',
              border: '1px solid var(--semi-color-danger)',
              borderRadius: '4px',
              background: 'transparent',
              color: 'var(--semi-color-danger)',
              cursor: 'pointer'
            }}
          >
            重试
          </button>
        </div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <div style={{
          height: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--semi-color-text-2)'
        }}>
          暂无数据
        </div>
      );
    }

    const CustomTooltip = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        const value = payload[0].value;
        const formattedValue = format === 'amount' ? formatValue(value, 'amount') : value.toLocaleString();
        return (
          <div style={{
            backgroundColor: 'var(--semi-color-bg-2)',
            border: '1px solid var(--semi-color-border)',
            borderRadius: '6px',
            padding: '8px 12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}>
            <p style={{ margin: '0 0 4px 0', color: 'var(--semi-color-text-1)' }}>{label}</p>
            <p style={{ margin: 0, color: 'var(--semi-color-text-0)', fontWeight: '600' }}>
              {formattedValue}
            </p>
          </div>
        );
      }
      return null;
    };

    return (
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
          <defs>
            <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--semi-color-border)" />
          <XAxis
            dataKey="name"
            stroke="var(--semi-color-text-2)"
            fontSize={12}
          />
          <YAxis
            stroke="var(--semi-color-text-2)"
            fontSize={12}
            tickFormatter={(value) => {
              if (format === 'amount') {
                return formatValue(value, 'amount');
              }
              return value.toLocaleString();
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill={`url(#gradient-${color})`}
            dot={{
              fill: color,
              stroke: '#fff',
              strokeWidth: 2,
              r: 4
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  if (loading) {
    return (
      <div className={styles.dashboardContent}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboardContent}>
      <h2>仪表盘</h2>
      <div className={styles.statsGrid}>
        {STAT_CARDS_CONFIG.map((card) => {
          const IconComponent = card.icon;
          const value = stats[card.id] || 0;

          return (
            <div
              key={card.id}
              className={styles.statCard}
              onClick={() => handleCardClick(card.navigateTo)}
              style={{ cursor: 'pointer' }}
            >
              <div className={styles.cardHeader}>
                <IconComponent size="large" style={{ color: card.color }} />
                <h3>{card.title}</h3>
              </div>
              <p>{formatValue(value, card.format)}</p>
            </div>
          );
        })}
      </div>

      {/* 趋势图表区域 */}
      <div className={styles.chartsSection}>
        <div className={styles.chartsHeader}>
          <h3>趋势分析</h3>
        </div>
        <div className={styles.chartsGrid}>
          {CHARTS_CONFIG.map((chart) => (
            <Card key={chart.id} title={chart.title} className={styles.chartCard}>
              {renderChart(
                chartData[chart.id] || [],
                chart.title,
                chart.color,
                chart.format
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;